import * as React from "react";
import {
  calculateLoadingText,
  calculateShouldShowContent,
  validateSecurityStep,
} from "../logic/paywall-calculations";
import { ParallelLoadingContainer } from "./ParallelLoadingContainer";
import { usePaywallStateManagerProvider } from "../../context/paywallStateManager.context";
import { useParentIframe } from "@trello-powerup-monorepo/iframe-messenger-v2";

export interface PaywallGuardContentProps {
  hideIframe?: boolean;
  overlayContent: (loadingText: any) => React.ReactNode;
  overlayStyling?: React.CSSProperties;
}

export const PaywallGuardContent: React.FC<
  React.PropsWithChildren<PaywallGuardContentProps>
> = ({ children, hideIframe, overlayContent, overlayStyling }) => {
  // AIDEV-NOTE: parallel-loading - Always render content for immediate loading, handle auth overlay separately
  const { isReady } = useParentIframe();
  const { securityStep } = usePaywallStateManagerProvider();
  // Use pure functions to calculate display state
  const shouldShowContent = calculateShouldShowContent(
    isReady,
    securityStep,
    hideIframe
  );
  const loadingText = calculateLoadingText(isReady, securityStep, hideIframe);

  // Orchestrate security validation
  React.useEffect(() => {
    // Use pure function to validate security step
    const validationError = validateSecurityStep(isReady, securityStep);

    // Orchestrate: decide when and how to handle validation errors
    if (validationError) {
      throw validationError;
    }
  }, [isReady, securityStep]);

  return (
    <ParallelLoadingContainer
      shouldShowContent={shouldShowContent}
      overlayContent={overlayContent && overlayContent(loadingText)}
      overlayStyling={overlayStyling}
    >
      <ParallelLoadingContainer.Content>
        {children}
      </ParallelLoadingContainer.Content>
      {loadingText && <ParallelLoadingContainer.Overlay text={loadingText} />}
    </ParallelLoadingContainer>
  );
};
